import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useStore } from '../lib/store';
import { Icon } from '../components/Icon';
import { Cover } from '../components/Cover';
import { IconBtn, TopBar } from '../components/ui';
import { VOICES } from '../lib/tts';
import { speakKokoro, stopKokoro, ensureAudioFromGesture, playTestBeep, getDiag, type KokoroVoice, type LoadProgress } from '../lib/kokoro';
import { getChapters, savePosition, putBook, getBook, type BookChapter } from '../lib/db';
import { pullProgress } from '../lib/sync';

const SAMPLE_PARAS_FALLBACK = [
  "I lie in bed, still trembling. You can wet the rim of a glass and run your finger around the rim and it will make a sound. This is what I feel like: this sound of glass. I feel like the word shatter.",
  "I want to be with someone. Lying in bed, with someone. It's so long since I did this. Tonight I can almost feel the old air around me, the air of before, which was loose and open and had windows in it that let things in, swinging doors, the back-and-forth of small breaths.",
  "We knew each other once, in a different life. We used to sit at the same kitchen table. We used to read aloud the same paragraphs from the same books, testing each other, trading meanings like bread.",
  "Luke is not with me and I cannot make him come. The air is hot and still. It smells of roses gone to flower, of bruised peaches. I lie in bed. What is the word for someone who remembers?",
  "There was a woman in my mother's kitchen once who said, the past is a strange country, you can only visit. You can never live there.",
  "A chair, a table, a lamp. Above, on the white ceiling, a relief ornament in the shape of a wreath, and in the center of it a blank space, plastered over, like the place in a face where the eye has been taken out.",
];

function splitSentences(text: string): string[] {
  return text.replace(/\s+/g, ' ').split(/(?<=[.!?])\s+(?=[A-Z"\u2018\u201C])/).map((s) => s.trim()).filter(Boolean);
}

function splitParagraphs(text: string): string[] {
  const p = text.split(/\n\s*\n|\r\n\r\n/).map((s) => s.trim()).filter(Boolean);
  if (p.length) return p;
  // Long block - split into ~3-sentence chunks for readability
  const sents = splitSentences(text);
  const out: string[] = [];
  for (let i = 0; i < sents.length; i += 3) out.push(sents.slice(i, i + 3).join(' '));
  return out;
}

const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];

export function Reader() {
  const nav = useNavigate();
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const requestedChapter = Number(searchParams.get('ch') || '');
  const book = useStore((s) => s.books.find((b) => b.id === id));
  const voice = useStore((s) => s.voice);
  const setVoice = useStore((s) => s.setVoice);
  const fontSize = useStore((s) => s.fontSize);
  const autoWpm = useStore((s) => s.autoScrollWpm);
  const saveHighlight = useStore((s) => s.saveHighlight);

  const [autoScroll, setAutoScroll] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [showVoice, setShowVoice] = useState(false);
  const [showSpeed, setShowSpeed] = useState(false);
  const [showHighlight, setShowHighlight] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDiag, setShowDiag] = useState(false);
  const [showChapters, setShowChapters] = useState(false);
  const [playerMinimized, setPlayerMinimized] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [loadProgress, setLoadProgress] = useState<LoadProgress | null>(null);
  const [currentSentence, setCurrentSentence] = useState(-1);
  const [scrollFrac, setScrollFrac] = useState(book?.progress || 0);
  const [diag, setDiag] = useState(() => getDiag());
  const [beepState, setBeepState] = useState<'idle' | 'playing' | 'ok' | 'fail'>('idle');
  const [busySince, setBusySince] = useState<number | null>(null);
  const [, setNow] = useState(0);
  const [chapters, setChapters] = useState<BookChapter[] | null>(null);
  const [chapterIdx, setChapterIdx] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  // User touch/wheel activity - auto-scroll is gated by 5s of quiet
  const userTouchTs = useRef(0);
  const savedOnceRef = useRef(false);
  const saveDebounceRef = useRef<number | null>(null);

  const kokoroVoiceMap: Record<string, KokoroVoice> = {
    bella: 'af_bella', michael: 'am_michael', nicole: 'af_nicole',
    sarah: 'af_sarah', adam: 'am_adam', emma: 'bf_emma',
  };

  // Load chapters + restore saved position, or skip front matter on first open.
  // Also pull the latest position from Supabase - if remote is newer (e.g. you
  // cleared site data or switched devices), adopt it before picking a chapter.
  useEffect(() => {
    if (!book) return;
    (async () => {
      // Always read the fresh record from IndexedDB - the React store book
      // prop is bootstrap-time and stale after any savePosition calls.
      const fresh = (await getBook(book.id)) || book;
      const cs = await getChapters(fresh.id);
      setChapters(cs.length ? cs : null);
      if (!cs.length) return;

      // Cache per-chapter word counts on the book so library progress can
      // show whole-book percentage (words read / total words) at a glance.
      let bookWithCounts = fresh;
      if (!fresh.chapter_words || fresh.chapter_words.length !== cs.length) {
        const chapter_words = cs.map((c) => (c.text || '').split(/\s+/).filter(Boolean).length);
        bookWithCounts = { ...fresh, chapter_words } as any;
        await putBook(bookWithCounts);
      }

      // Merge remote progress if newer than local
      const localTs = bookWithCounts.position?.ts ?? 0;
      const remote = await pullProgress(fresh.id, localTs).catch(() => null);
      const effectivePos = remote
        ? { ch: remote.ch, sent: remote.sent, scroll: remote.scroll, ts: remote.ts }
        : bookWithCounts.position;
      if (remote) {
        const progress = (await import('../lib/db')).computeProgressFraction(bookWithCounts.chapter_words, remote.ch, remote.scroll);
        await putBook({ ...bookWithCounts, position: effectivePos as any, progress });
      }

      if (Number.isFinite(requestedChapter) && requestedChapter >= 0 && requestedChapter < cs.length) {
        setChapterIdx(requestedChapter);
      } else if (effectivePos && effectivePos.ch >= 0 && effectivePos.ch < cs.length) {
        setChapterIdx(effectivePos.ch);
        if (effectivePos.sent >= 0) setCurrentSentence(effectivePos.sent);
      } else {
        const firstReal = cs.findIndex((c) => !/\(front matter\)/i.test(c.title) && c.text.length > 800);
        setChapterIdx(firstReal >= 0 ? firstReal : 0);
      }
    })();
  }, [book?.id, requestedChapter]);

  // Persist position (debounced)
  const scheduleSave = () => {
    if (!book) return;
    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = window.setTimeout(async () => {
      const scroll = scrollRef.current;
      const frac = scroll ? (scroll.scrollTop / Math.max(1, scroll.scrollHeight - scroll.clientHeight)) : 0;
      // Blend sentence + chapter into an overall progress fraction
      const totalCh = Math.max(1, chapters?.length || 1);
      const chFrac = chapterIdx / totalCh + (1 / totalCh) * frac;
      await savePosition(book.id, chapterIdx, Math.max(0, currentSentence), chFrac);
    }, 400) as unknown as number;
  };

  // Resolve paragraphs for the current chapter/fallback
  const paragraphs = useMemo(() => {
    if (chapters && chapters[chapterIdx]) return splitParagraphs(chapters[chapterIdx].text);
    return SAMPLE_PARAS_FALLBACK;
  }, [chapters, chapterIdx]);

  // Flatten paragraphs -> sentences
  const sentences = useMemo(() => {
    const out: { text: string; pIdx: number; sIdx: number; globalIdx: number }[] = [];
    let g = 0;
    paragraphs.forEach((p, pIdx) => {
      splitSentences(p).forEach((text, sIdx) => out.push({ text, pIdx, sIdx, globalIdx: g++ }));
    });
    return out;
  }, [paragraphs]);

  // Reset playback cursor when changing chapter (scroll-to-top unless a saved
  // position exists for this chapter, which the restore effect handles).
  useEffect(() => {
    stopKokoro();
    setPlaying(false);
    setCurrentSentence(-1);
    savedOnceRef.current = false; // allow restore on the new chapter
    scrollRef.current?.scrollTo({ top: 0 });
  }, [chapterIdx]);

  // Restore saved sentence + scroll once paragraphs render for this chapter
  useEffect(() => {
    if (!book || !sentences.length || savedOnceRef.current) return;
    const pos = book.position;
    if (pos && pos.ch === chapterIdx) {
      const container = scrollRef.current;
      if (container && pos.scroll > 0) {
        requestAnimationFrame(() => {
          const max = container.scrollHeight - container.clientHeight;
          container.scrollTop = pos.scroll * max;
        });
      }
    }
    savedOnceRef.current = true;
  }, [book?.id, chapterIdx, sentences.length]);

  // Live elapsed counter
  useEffect(() => {
    if (!loadProgress || loadProgress.status === 'ready' || loadProgress.status === 'error') { setBusySince(null); return; }
    if (busySince === null) setBusySince(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(t);
  }, [loadProgress]);

  // Auto-scroll when TTS NOT playing (silent reading timer-based)
  useEffect(() => {
    if (!autoScroll || !scrollRef.current || playing) return;
    const pxPerSec = (autoWpm / 60) * 2.4 * speed;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      const el = scrollRef.current;
      if (el) {
        el.scrollTop += pxPerSec * dt;
        if (el.scrollTop >= el.scrollHeight - el.clientHeight) setAutoScroll(false);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [autoScroll, autoWpm, speed, playing]);

  // Track scroll fraction + save progress on scroll stop
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const max = el.scrollHeight - el.clientHeight;
      setScrollFrac(max > 0 ? Math.min(1, el.scrollTop / max) : 0);
      scheduleSave();
    };
    // Only REAL user input locks auto-scroll. Programmatic scrollTo doesn't fire these.
    const onUserTouch = () => { userTouchTs.current = Date.now(); };
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('touchstart', onUserTouch, { passive: true });
    el.addEventListener('touchmove', onUserTouch, { passive: true });
    el.addEventListener('wheel', onUserTouch, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('touchstart', onUserTouch);
      el.removeEventListener('touchmove', onUserTouch);
      el.removeEventListener('wheel', onUserTouch);
    };
  }, [chapterIdx]);

  // Auto-follow: scroll to current sentence, but ONLY after 5s of no user touch
  useEffect(() => {
    if (currentSentence < 0 || !autoScroll) return;
    const doScroll = () => {
      const quietFor = Date.now() - userTouchTs.current;
      if (quietFor < 5000) {
        // user touched recently, retry when the quiet window opens
        const retry = window.setTimeout(doScroll, 5000 - quietFor + 100);
        return () => clearTimeout(retry);
      }
      const container = scrollRef.current;
      const el = document.getElementById(`sent-${currentSentence}`);
      if (!el || !container) return;
      const target = el.offsetTop - container.clientHeight * 0.33;
      container.scrollTo({ top: target, behavior: 'smooth' });
    };
    doScroll();
  }, [currentSentence, autoScroll]);

  // Save progress when sentence or chapter advances
  useEffect(() => { if (book) scheduleSave(); }, [currentSentence, chapterIdx]);

  // TTS lifecycle
  useEffect(() => () => stopKokoro(), []);

  const startAt = (idx: number, withVoice: KokoroVoice, withSpeed: number) => {
    const startIdx = Math.max(0, Math.min(idx, sentences.length - 1));
    setPlaying(true);
    setCurrentSentence(startIdx);
    // Pause any background offline download so the Reader gets full
    // server bandwidth. offline-audio.ts's worker checks this flag.
    (window as any).__doomReaderPlaying = true;
    // Pass an array so Kokoro's i in onSentence(i, total) maps 1:1 to our
    // sentences[] indices. Highlight always matches what's playing.
    const remaining = sentences.slice(startIdx).map((s) => s.text);
    speakKokoro(remaining, {
      voice: withVoice,
      speed: withSpeed,
      onLoad: (p) => setLoadProgress(p),
      onSentence: (i) => setCurrentSentence(startIdx + i),
      onEnd: () => {
        setPlaying(false); setLoadProgress(null); setCurrentSentence(-1);
        (window as any).__doomReaderPlaying = false;
      },
      bookId: book?.id,
      chapterIdx,
      sentenceStartIdx: startIdx,
    }).catch((e) => {
      console.error('[reader] Kokoro speak failed', e);
      setPlaying(false);
      setLoadProgress({ status: 'error', pct: 0, detail: String(e?.message || e) });
      (window as any).__doomReaderPlaying = false;
    });
  };

  const skipBy = (delta: number) => {
    const next = Math.max(0, Math.min(sentences.length - 1, (currentSentence < 0 ? 0 : currentSentence) + delta));
    jumpTo(next);
  };

  // Jump to a specific sentence (tap on text). If TTS is playing, restart
  // audio at that position. Otherwise just move the highlight cursor.
  const jumpTo = (idx: number) => {
    const next = Math.max(0, Math.min(sentences.length - 1, idx));
    setCurrentSentence(next);
    if (playing) {
      stopKokoro();
      setTimeout(() => startAt(next, kokoroVoiceMap[voice] || 'af_bella', speed), 60);
    }
  };

  const togglePlay = () => {
    try { (window as any).__doomLog?.('[reader] togglePlay tap, playing=' + playing + ' sentences=' + sentences.length); } catch {}
    if (playing) {
      stopKokoro();
      setPlaying(false);
      setLoadProgress(null);
      (window as any).__doomReaderPlaying = false;
      return;
    }
    setLoadProgress({ status: 'init', pct: 0, detail: 'Tap received, starting...' });
    ensureAudioFromGesture();
    const startIdx = currentSentence >= 0 ? currentSentence : 0;
    startAt(startIdx, kokoroVoiceMap[voice] || 'af_bella', speed);
  };

  const applyVoice = (newVoice: string) => {
    setVoice(newVoice);
    if (playing) {
      stopKokoro();
      setTimeout(() => startAt(Math.max(0, currentSentence), kokoroVoiceMap[newVoice] || 'af_bella', speed), 80);
    }
  };
  const applySpeed = (newSpeed: number) => {
    setSpeed(newSpeed);
    if (playing) {
      stopKokoro();
      setTimeout(() => startAt(Math.max(0, currentSentence), kokoroVoiceMap[voice] || 'af_bella', newSpeed), 80);
    }
  };

  // Selection -> highlight popup
  useEffect(() => {
    const onUp = () => {
      const sel = window.getSelection?.();
      const text = sel?.toString().trim();
      if (text && text.length > 4) {
        setSelected(text);
        setShowHighlight(true);
      }
    };
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchend', onUp);
    return () => {
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchend', onUp);
    };
  }, []);

  if (!book) return <div className="p-8">book not found</div>;

  const playerFrac = sentences.length > 0 && currentSentence >= 0 ? (currentSentence + 1) / sentences.length : 0;
  const chapterLabel = chapters && chapters[chapterIdx]
    ? `Ch ${chapterIdx + 1} · ${chapters[chapterIdx].title}`
    : 'Ch 21 · Night';

  return (
    <div className="flex flex-col h-full bg-paper dark:bg-night-900 safe-top">
      <TopBar
        left={<IconBtn name="back" onClick={() => nav(-1)} />}
        title={
          <button onClick={() => chapters && setShowChapters(true)} className="font-serif flex items-center gap-1 max-w-[220px]">
            <span className="truncate">{chapterLabel}</span>
            {chapters && <Icon name="chevRight" className="w-3 h-3 rotate-90 shrink-0 opacity-60" />}
          </button>
        }
        right={
          <>
            <button onClick={() => setAutoScroll((v) => !v)} className={`h-9 px-3 rounded-full text-[11px] font-medium flex items-center gap-1.5 ${autoScroll ? 'bg-forest-500 text-cream-50' : 'bg-black/5 dark:bg-white/10'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${autoScroll ? 'bg-coral animate-pulse' : 'bg-ink-500'}`} />Auto
            </button>
            <IconBtn name="settings" onClick={() => setShowSettings(true)} />
          </>
        }
      />

      {showDiag && (
        <div className="mx-5 mt-2 rounded-xl bg-cream-100 dark:bg-night-700 p-3 text-[11px] font-mono space-y-1 text-ink-700 dark:text-ink-300">
          <div>device: <b>{diag.device || '-'}</b></div>
          <div>ctx: <b>{diag.ctxState}</b></div>
          <div>loaded: <b>{String(diag.loaded)}</b></div>
          <div>error: <span className="text-coral">{diag.lastError || 'none'}</span></div>
          <div className="pt-1 flex flex-wrap gap-2">
            <button
              onClick={async () => {
                setBeepState('playing');
                try { await playTestBeep(); setBeepState('ok'); } catch { setBeepState('fail'); }
                setDiag(getDiag());
              }}
              className={`h-8 px-3 rounded-full text-[10px] font-semibold ${beepState === 'ok' ? 'bg-forest-500 text-cream-50' : beepState === 'fail' ? 'bg-coral text-cream-50' : 'bg-ink-900 text-cream-50 dark:bg-cream-100 dark:text-ink-900'}`}
            >
              {beepState === 'idle' && '🔊 Beep'}
              {beepState === 'playing' && 'Playing...'}
              {beepState === 'ok' && '✓ Beep OK'}
              {beepState === 'fail' && '✗ Beep fail'}
            </button>
            <button onClick={() => setDiag(getDiag())} className="h-8 px-3 rounded-full bg-black/5 dark:bg-white/10 text-[10px]">refresh</button>
          </div>
        </div>
      )}

      <div
        ref={scrollRef}
        className={`flex-1 overflow-y-auto px-7 pt-4 no-scrollbar serif-reader text-ink-900 dark:text-cream-100 ${playerMinimized ? 'pb-24' : 'pb-44'}`}
        style={{ fontSize }}
      >
        {chapters && chapters[chapterIdx] ? (
          <>
            <div className="text-[11px] uppercase tracking-[.25em] text-ink-400 font-sans mb-3">Chapter {chapterIdx + 1}</div>
            <h2 className="font-display text-[28px] leading-tight mb-5">{chapters[chapterIdx].title}</h2>
          </>
        ) : (
          <>
            <div className="text-[11px] uppercase tracking-[.25em] text-ink-400 font-sans mb-3">Chapter Twenty-One</div>
            <h2 className="font-display text-[32px] leading-tight mb-5">Night</h2>
          </>
        )}
        {paragraphs.map((p, pIdx) => {
          const sents = splitSentences(p);
          const baseIdx = sentences.findIndex((s) => s.pIdx === pIdx);
          return (
            <p key={pIdx} className={`mb-6 leading-[1.75] ${pIdx === 0 ? 'drop-cap' : ''}`}>
              {sents.map((s, sIdx) => {
                const gIdx = baseIdx + sIdx;
                const active = gIdx === currentSentence;
                return (
                  <span
                    key={sIdx}
                    id={`sent-${gIdx}`}
                    onClick={() => {
                      // Don't jump if the tap actually made a text selection
                      if (window.getSelection()?.toString().trim()) return;
                      jumpTo(gIdx);
                    }}
                    className={`transition-colors cursor-pointer ${active ? 'bg-coral/25 rounded px-0.5 -mx-0.5' : ''}`}
                  >
                    {s}{sIdx < sents.length - 1 ? ' ' : ''}
                  </span>
                );
              })}
            </p>
          );
        })}
        {chapters && (
          <div className="flex gap-2 mt-8 mb-4">
            {chapterIdx > 0 && (
              <button onClick={() => setChapterIdx((i) => Math.max(0, i - 1))} className="flex-1 h-11 rounded-full bg-paper dark:bg-night-800 shadow-soft text-[13px] font-medium flex items-center justify-center gap-2">
                <Icon name="chevRight" className="w-3 h-3 rotate-180" />Previous
              </button>
            )}
            {chapterIdx < chapters.length - 1 && (
              <button onClick={() => setChapterIdx((i) => Math.min(chapters.length - 1, i + 1))} className="flex-1 h-11 rounded-full bg-ink-900 dark:bg-cream-100 text-cream-50 dark:text-ink-900 text-[13px] font-medium flex items-center justify-center gap-2">
                Next chapter<Icon name="chevRight" className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Player (minimizable) */}
      <div className="absolute left-0 right-0 bottom-0 px-4 pb-6 safe-bottom">
        {loadProgress && loadProgress.status !== 'ready' && (
          <div className={`mb-2 rounded-2xl px-3 py-2 text-[11px] flex items-center gap-2 shadow-card ${loadProgress.status === 'error' ? 'bg-coral text-cream-50' : 'bg-forest-500 text-cream-50'}`}>
            {loadProgress.status !== 'error' && <div className="waveform flex h-3"><span /><span /><span /></div>}
            <span className="flex-1 break-words">
              {loadProgress.status === 'init' && (loadProgress.detail || 'Loading voice...')}
              {loadProgress.status === 'downloading' && `Downloading model · ${loadProgress.pct}%${loadProgress.detail ? ' · ' + loadProgress.detail : ''}`}
              {loadProgress.status === 'error' && <><b>Voice error</b><br /><span className="opacity-90 font-mono text-[10px]">{loadProgress.detail}</span></>}
            </span>
            {loadProgress.status === 'error' && (
              <button onClick={() => setLoadProgress(null)} className="shrink-0 w-5 h-5 rounded-full bg-cream-50/20 flex items-center justify-center">
                <Icon name="close" className="w-3 h-3" />
              </button>
            )}
          </div>
        )}

        {playerMinimized ? (
          <button onClick={() => setPlayerMinimized(false)} className="w-full rounded-full bg-ink-900/95 dark:bg-cream-100/10 dark:backdrop-blur-xl text-cream-50 shadow-card px-4 py-2.5 flex items-center gap-3">
            <button onClick={(e) => { e.stopPropagation(); togglePlay(); }} className="w-8 h-8 rounded-full bg-cream-50 text-ink-900 flex items-center justify-center">
              <Icon name={playing ? 'pause' : 'play'} className="w-3.5 h-3.5" strokeWidth={0} />
            </button>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-[11px] opacity-70 truncate">{playing ? `${VOICES.find((v) => v.id === voice)?.name} · ${speed}×` : 'Tap to expand player'}</div>
              <div className="h-[2px] mt-1 rounded-full bg-cream-50/20 overflow-hidden">
                <div className="h-full bg-coral" style={{ width: `${Math.round(playerFrac * 100)}%` }} />
              </div>
            </div>
            <div className="text-[10px] opacity-60">▲</div>
          </button>
        ) : (
          <div className="rounded-3xl bg-ink-900/95 dark:bg-cream-100/10 dark:backdrop-blur-xl text-cream-50 shadow-card p-3">
            <div className="flex items-center gap-3">
              <Cover kind={book.cover_kind} dataUrl={book.cover_data} className="w-10 h-14 rounded-md shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium truncate">{book.title}</div>
                <div className="text-[10px] opacity-60 truncate">
                  {playing ? `Reading aloud · ${VOICES.find((v) => v.id === voice)?.name || 'Bella'}` : 'Tap play to listen'}
                </div>
              </div>
              <button onClick={() => setPlayerMinimized(true)} className="w-8 h-8 rounded-full bg-cream-50/15 flex items-center justify-center text-[10px]">▼</button>
              <button onClick={togglePlay} className="w-10 h-10 rounded-full bg-cream-50 text-ink-900 flex items-center justify-center">
                <Icon name={playing ? 'pause' : 'play'} className="w-4 h-4" strokeWidth={0} />
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <div className="flex-1 h-[3px] rounded-full bg-cream-50/20 relative">
                <div className="h-full rounded-full bg-coral" style={{ width: `${Math.round(playerFrac * 100)}%` }} />
              </div>
              <span className="text-[10px] opacity-60 tabular-nums">{currentSentence >= 0 ? `${currentSentence + 1}/${sentences.length}` : `${sentences.length} sent.`}</span>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <button onClick={() => setShowVoice(true)} className="flex items-center gap-1.5 text-[11px] opacity-80">
                <span className={`w-1.5 h-1.5 rounded-full ${playing ? 'bg-coral animate-pulse' : 'bg-cream-50/40'}`} />
                {VOICES.find((v) => v.id === voice)?.name || 'Bella'}
              </button>
              <div className="flex items-center gap-5">
                <button onClick={() => skipBy(-1)}><Icon name="skipPrev" className="w-4 h-4" strokeWidth={0} /></button>
                <button onClick={() => setShowSpeed(true)} className="text-[11px] font-semibold tabular-nums px-2 h-6 rounded-md bg-cream-50/15">{speed}×</button>
                <button onClick={() => skipBy(1)}><Icon name="skipNext" className="w-4 h-4" strokeWidth={0} /></button>
              </div>
              <button onClick={() => { setSelected(sentences[currentSentence >= 0 ? currentSentence : 0]?.text || ''); setShowHighlight(true); }} className="w-7 h-7 rounded-full bg-cream-50/15 flex items-center justify-center">
                <Icon name="highlight" className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showChapters && chapters && <ChapterSheet chapters={chapters} active={chapterIdx} onPick={(i) => { setChapterIdx(i); setShowChapters(false); }} onClose={() => setShowChapters(false)} />}
        {showSpeed && <SpeedSheet active={speed} onPick={applySpeed} onClose={() => setShowSpeed(false)} />}
        {showVoice && <VoiceSheet active={voice} onPick={(id) => { applyVoice(id); setShowVoice(false); }} onClose={() => setShowVoice(false)} />}
        {showHighlight && selected && (
          <HighlightPopup
            text={selected}
            onClose={() => { setShowHighlight(false); window.getSelection?.()?.removeAllRanges(); }}
            onSave={async (kind, note) => {
              await saveHighlight({ book_id: book.id, chapter: chapterIdx + 1, page: Math.round(scrollFrac * book.pages), text: selected, note, kind });
              setShowHighlight(false);
            }}
          />
        )}
        {showSettings && <SettingsSheet onClose={() => setShowSettings(false)} />}
      </AnimatePresence>
    </div>
  );
}

/* ================== Sheets ================== */

function ChapterSheet({ chapters, active, onPick, onClose }: { chapters: BookChapter[]; active: number; onPick: (i: number) => void; onClose: () => void }) {
  return (
    <div className="absolute inset-0 z-40 flex items-end">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', stiffness: 260, damping: 28 }} className="relative w-full bg-paper dark:bg-night-800 rounded-t-[32px] shadow-sheet pt-3 pb-8 max-h-[72%] overflow-y-auto no-scrollbar safe-bottom">
        <div className="mx-auto w-10 h-1 rounded-full bg-ink-300/60 mb-3" />
        <div className="px-6 flex items-center justify-between mb-3">
          <h3 className="font-display text-[22px]">Chapters</h3>
          <IconBtn name="close" onClick={onClose} />
        </div>
        <div className="px-4 space-y-1">
          {chapters.map((c, i) => (
            <button
              key={i}
              onClick={() => onPick(i)}
              className={`w-full flex items-center gap-3 p-3 rounded-2xl text-left transition ${active === i ? 'bg-ink-900 text-cream-50 dark:bg-cream-100 dark:text-ink-900' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold tabular-nums ${active === i ? 'bg-cream-50/20 text-cream-50 dark:bg-ink-900/20 dark:text-ink-900' : 'bg-black/5 dark:bg-white/10 text-ink-500'}`}>{i + 1}</div>
              <div className="flex-1 min-w-0 text-[13px] font-medium truncate">{c.title || `Chapter ${i + 1}`}</div>
              <Icon name="chevRight" className="w-4 h-4 opacity-40" />
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function SpeedSheet({ active, onPick, onClose }: { active: number; onPick: (n: number) => void; onClose: () => void }) {
  const opts = [0.75, 1, 1.1, 1.25, 1.5, 1.75, 2];
  return (
    <div className="absolute inset-0 z-40 flex items-end">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', stiffness: 260, damping: 28 }} className="relative w-full bg-paper dark:bg-night-800 rounded-t-[32px] shadow-sheet pt-3 pb-8 safe-bottom">
        <div className="mx-auto w-10 h-1 rounded-full bg-ink-300/60 mb-3" />
        <div className="px-6 flex items-center justify-between mb-3">
          <h3 className="font-display text-[22px]">Playback speed</h3>
          <IconBtn name="close" onClick={onClose} />
        </div>
        <div className="px-6 grid grid-cols-3 gap-2">
          {opts.map((s) => (
            <button key={s} onClick={() => { onPick(s); onClose(); }} className={`h-14 rounded-2xl text-[15px] font-semibold tabular-nums transition ${active === s ? 'bg-ink-900 text-cream-50 dark:bg-cream-100 dark:text-ink-900' : 'bg-cream-100 dark:bg-night-700'}`}>{s}×</button>
          ))}
        </div>
        <p className="px-6 mt-3 text-[11px] text-ink-500">Capped at 2× - Kokoro gets robotic above. Speed change resumes from the current sentence.</p>
      </motion.div>
    </div>
  );
}

function VoiceSheet({ active, onPick, onClose }: { active: string; onPick: (id: string) => void; onClose: () => void }) {
  const [picked, setPicked] = useState(active);
  return (
    <div className="absolute inset-0 z-40 flex items-end">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', stiffness: 260, damping: 28 }} className="relative w-full bg-paper dark:bg-night-800 rounded-t-[32px] shadow-sheet pt-3 pb-8 max-h-[72%] safe-bottom">
        <div className="mx-auto w-10 h-1 rounded-full bg-ink-300/60 mb-3" />
        <div className="px-6 flex items-center justify-between mb-1">
          <div>
            <h3 className="font-display text-[22px]">Voice</h3>
            <p className="text-[12px] text-ink-500">Kokoro · on-device</p>
          </div>
          <IconBtn name="close" onClick={onClose} />
        </div>
        <div className="px-6 mt-4 grid grid-cols-2 gap-3">
          {VOICES.map((v) => (
            <button key={v.id} onClick={() => setPicked(v.id)} className={`relative p-4 rounded-2xl text-left transition ${picked === v.id ? 'ring-2 ring-forest-500' : ''}`} style={{ background: v.color + '1a' }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center mb-3 text-cream-50" style={{ background: v.color }}>
                <span className="text-[13px] font-semibold">{v.name[0]}</span>
              </div>
              <div className="text-[14px] font-medium">{v.name}</div>
              <div className="text-[11px] text-ink-500 mt-0.5">{v.sub}</div>
            </button>
          ))}
        </div>
        <div className="px-6 mt-5">
          <button onClick={() => onPick(picked)} className="w-full h-12 rounded-full bg-ink-900 dark:bg-cream-100 text-cream-50 dark:text-ink-900 font-medium text-[14px]">
            Use {VOICES.find((v) => v.id === picked)?.name}
          </button>
          <p className="text-[11px] text-ink-500 mt-2 text-center">Switches voice and resumes from the current sentence.</p>
        </div>
      </motion.div>
    </div>
  );
}

function HighlightPopup({ text, onClose, onSave }: { text: string; onClose: () => void; onSave: (kind: 'quote' | 'learning', note: string | null) => Promise<void> }) {
  const [hint, setHint] = useState('');
  return (
    <div className="absolute inset-0 z-40">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" />
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} transition={{ type: 'spring', stiffness: 320, damping: 28 }} className="absolute left-4 right-4 bottom-6 rounded-3xl bg-paper dark:bg-night-800 shadow-sheet p-4 safe-bottom">
        <div className="p-3 rounded-2xl bg-cream-100 dark:bg-night-700 mb-3">
          <div className="text-[11px] uppercase tracking-[.15em] text-ink-500 mb-1.5">Selected</div>
          <p className="font-serif text-[14px] leading-snug">"<span className="hl-highlight">{text}</span>"</p>
        </div>
        <div className="flex items-center gap-2 p-3 rounded-2xl bg-cream-100 dark:bg-night-700 mb-3">
          <Icon name="sparkle" className="w-4 h-4 text-forest-500 shrink-0" />
          <input value={hint} onChange={(e) => setHint(e.target.value)} placeholder="Add context · save to..." className="flex-1 bg-transparent text-[13px] placeholder:text-ink-400 focus:outline-none" />
        </div>
        <div className="grid grid-cols-4 gap-2">
          {([
            { i: 'bookmark', l: 'Quote', kind: 'quote', c: 'bg-coral text-cream-50' },
            { i: 'sparkle', l: 'Learning', kind: 'learning', c: 'bg-forest-500 text-cream-50' },
            { i: 'copy', l: 'Copy', kind: null, c: 'bg-black/5 dark:bg-white/10' },
            { i: 'share', l: 'Share', kind: null, c: 'bg-black/5 dark:bg-white/10' },
          ] as const).map((a) => (
            <button
              key={a.l}
              onClick={async () => {
                if (a.kind) await onSave(a.kind, hint || null);
                else if (a.l === 'Copy') { await navigator.clipboard?.writeText(text); onClose(); }
                else onClose();
              }}
              className={`h-16 rounded-2xl flex flex-col items-center justify-center gap-1 ${a.c}`}
            >
              <Icon name={a.i as any} className="w-4 h-4" />
              <span className="text-[11px] font-medium">{a.l}</span>
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between text-[11px] text-ink-500 px-1">
          <span>Syncs to SiYuan · Life OS · Second Brain</span>
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-forest-500" />{navigator.onLine ? 'Online' : 'Offline'}</span>
        </div>
      </motion.div>
    </div>
  );
}

function SettingsSheet({ onClose }: { onClose: () => void }) {
  const { theme, setTheme, font, setFont, fontSize, setFontSize, autoScrollWpm, setAutoScrollWpm, voice } = useStore();
  return (
    <div className="absolute inset-0 z-40 flex items-end">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', stiffness: 260, damping: 28 }} className="relative w-full bg-paper dark:bg-night-800 rounded-t-[32px] shadow-sheet pt-3 pb-8 max-h-[80%] overflow-y-auto no-scrollbar safe-bottom">
        <div className="mx-auto w-10 h-1 rounded-full bg-ink-300/60 mb-3" />
        <div className="px-6 flex items-center justify-between mb-4">
          <h3 className="font-display text-[22px]">Reading settings</h3>
          <IconBtn name="close" onClick={onClose} />
        </div>
        <div className="px-6 mb-5">
          <div className="text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2">Preview</div>
          <div className="rounded-2xl bg-cream-100 dark:bg-night-700 p-4" style={{ fontFamily: font, fontSize }}>
            <span className="drop-cap">The</span> quieter you become, the more you are able to hear.
          </div>
        </div>
        <div className="px-6 mb-5">
          <div className="text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2">Font</div>
          <div className="grid grid-cols-4 gap-2">
            {[{ name: 'Fraunces', sub: 'Editorial' }, { name: 'Instrument Serif', sub: 'Classic' }, { name: 'Georgia', sub: 'Serif' }, { name: 'Inter', sub: 'Sans' }].map((f) => (
              <button key={f.name} onClick={() => setFont(f.name)} className={`h-20 rounded-2xl flex flex-col items-center justify-center gap-0.5 transition ${font === f.name ? 'bg-ink-900 text-cream-50 dark:bg-cream-100 dark:text-ink-900' : 'bg-cream-100 dark:bg-night-700'}`}>
                <span className="text-[22px]" style={{ fontFamily: f.name }}>Aa</span>
                <span className="text-[10px] opacity-70">{f.sub}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="px-6 mb-5">
          <div className="flex justify-between text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2">
            <span>Size</span><span className="tabular-nums">{fontSize}px</span>
          </div>
          <input type="range" min={12} max={22} value={fontSize} onChange={(e) => setFontSize(Number(e.target.value))} className="w-full accent-ink-900 dark:accent-cream-100" />
        </div>
        <div className="px-6 mb-5">
          <div className="text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2">Theme</div>
          <div className="grid grid-cols-3 gap-2">
            {[{ id: 'light' as const, label: 'Paper', bg: '#FAF7F0', fg: '#1A1A1A' }, { id: 'sepia' as const, label: 'Sepia', bg: '#EEE1C6', fg: '#3A2515' }, { id: 'dark' as const, label: 'Night', bg: '#14120F', fg: '#F1EBDC' }].map((t) => (
              <button key={t.id} onClick={() => setTheme(t.id)} className={`h-20 rounded-2xl flex flex-col items-center justify-center gap-1 border ${theme === t.id ? 'border-forest-500' : 'border-transparent'}`} style={{ background: t.bg, color: t.fg }}>
                <span className="font-serif text-[22px]">Aa</span>
                <span className="text-[10px]">{t.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="px-6 mb-5">
          <div className="flex justify-between text-[11px] uppercase tracking-[.2em] text-ink-500 mb-2">
            <span>Auto-scroll speed</span><span className="tabular-nums">{autoScrollWpm} wpm</span>
          </div>
          <input type="range" min={120} max={400} step={10} value={autoScrollWpm} onChange={(e) => setAutoScrollWpm(Number(e.target.value))} className="w-full accent-coral" />
        </div>
        <div className="px-6 mb-2">
          <button className="w-full flex items-center justify-between p-4 rounded-2xl bg-cream-100 dark:bg-night-700">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-coral text-cream-50 flex items-center justify-center text-[14px] font-semibold">{(VOICES.find((v) => v.id === voice)?.name || 'B')[0]}</div>
              <div className="text-left">
                <div className="text-[13px] font-medium">{VOICES.find((v) => v.id === voice)?.name || 'Bella'}</div>
                <div className="text-[11px] text-ink-500">Kokoro · on-device</div>
              </div>
            </div>
            <Icon name="chevRight" className="w-4 h-4 text-ink-400" />
          </button>
        </div>
      </motion.div>
    </div>
  );
}
